import React, { useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../../hooks/useAuth';
import { getFormattedDate } from '../../assets/Utils';
import LeaveRequestList from './LeaveRequestList';
import EmployeeLeaveVouchers from './EmployeeLeaveVouchers';
import EmployeeLeaveBalances from './EmployeeLeaveBalances';
import EmployeeServiceAccruals from './EmployeeServiceAccruals';
import '../../styles/EmployeeCard.css';

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

const showDate = (value) => {
  if (!value) return 'None';
  const text = String(value).slice(0, 10);
  const match = text.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!match) return getFormattedDate(value);
  return `${match[3]} ${MONTHS[Number(match[2]) - 1]} ${match[1]}`;
};

const money = (value) => {
  const amount = Number(value);
  if (!Number.isFinite(amount)) return 'None';
  return amount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const yesNo = (value) => (value ? 'Yes' : 'No');

const EmployeeCard = () => {
  const { state } = useLocation();
  const { hasPermission } = useAuth();
  const canViewPay = hasPermission('/employee-list/pay', 'View');
  const passedEmployee = state?.employee || null;
  const employeeId = passedEmployee?.employee_id;
  const [employee, setEmployee] = useState(passedEmployee);
  const [loading, setLoading] = useState(Boolean(employeeId));
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState('');

  useEffect(() => {
    if (!employeeId) {
      setEmployee(null);
      setLoading(false);
      return undefined;
    }

    let cancelled = false;
    setLoading(true);
    axios.get(
      `${process.env.REACT_APP_SERVER_BASE_URL}/hospital-portal/api/employees/byID/${employeeId}`
    )
      .then((response) => {
        if (!cancelled) {
          setEmployee(response.data);
          setError('');
        }
      })
      .catch((err) => {
        console.error(err);
        if (!cancelled) setError('Could not load this employee.');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [employeeId]);

  const tabs = useMemo(() => [
    hasPermission('/leave-requests', 'View') && { id: 'leave', label: 'Leave requests' },
    hasPermission('/leave-requests', 'View') && { id: 'vouchers', label: 'Payment vouchers' },
    hasPermission('/leave-balances', 'View') && { id: 'balances', label: 'Leave balances' },
    hasPermission('/employee-benefits', 'View') && { id: 'service', label: 'End of service' },
  ].filter(Boolean), [hasPermission]);

  useEffect(() => {
    if (!tabs.length) return;
    if (!tabs.some((tab) => tab.id === activeTab)) setActiveTab(tabs[0].id);
  }, [tabs, activeTab]);

  if (!employeeId) {
    return (
      <div className="employee-card">
        <p className="employee-card-message">Choose an employee from the Employee List.</p>
      </div>
    );
  }

  if (loading && !employee) {
    return (
      <div className="employee-card">
        <p className="employee-card-message">Loading employee...</p>
      </div>
    );
  }

  if (!employee) {
    return (
      <div className="employee-card">
        <p className="employee-card-message">{error || 'Employee not found.'}</p>
      </div>
    );
  }

  const renderActiveTab = () => {
    switch (activeTab) {
      case 'leave':
        return <LeaveRequestList employee={employee} />;
      case 'vouchers':
        return <EmployeeLeaveVouchers employee={employee} />;
      case 'balances':
        return <EmployeeLeaveBalances employee={employee} />;
      case 'service':
        return <EmployeeServiceAccruals employee={employee} />;
      default:
        return null;
    }
  };

  return (
    <div className="employee-card">
      <div className="employee-card-header">
        <h2>{employee.employee_name}</h2>
        {error && <p className="leave-error">{error}</p>}
        <dl className="employee-details">
          <div>
            <dt>Role</dt>
            <dd>{employee.role || 'None'}</dd>
          </div>
          <div>
            <dt>Department</dt>
            <dd>{employee.department || 'None'}</dd>
          </div>
          <div>
            <dt>Branch</dt>
            <dd>{employee.branch || 'None'}</dd>
          </div>
          <div>
            <dt>Hire date</dt>
            <dd>{showDate(employee.hire_date)}</dd>
          </div>
          <div>
            <dt>Manager</dt>
            <dd>{employee.manager_name || 'None'}</dd>
          </div>
          <div>
            <dt>Status</dt>
            <dd>{employee.status ? 'Active' : 'Not active'}</dd>
          </div>
          <div>
            <dt>Sex</dt>
            <dd>{employee.sex || 'None'}</dd>
          </div>
          <div>
            <dt>Saturday work</dt>
            <dd>{yesNo(employee.works_saturday)}</dd>
          </div>
          <div>
            <dt>Base pay</dt>
            <dd>{money(employee.base_pay)}</dd>
          </div>
          <div>
            <dt>Other entitlements</dt>
            <dd>{canViewPay ? money(employee.other_entitlement) : '—'}</dd>
          </div>
          <div>
            <dt>Salary</dt>
            <dd>{canViewPay ? money(employee.salary) : '—'}</dd>
          </div>
          <div>
            <dt>NASSIT number</dt>
            <dd>{employee.nassit_ssn || 'None'}</dd>
          </div>
          <div>
            <dt>NASSIT exempt</dt>
            <dd>{yesNo(employee.nassit_exempt)}</dd>
          </div>
        </dl>
      </div>
      {tabs.length > 0 && (
        <>
          <div className="employee-card-tabs">
            <ul>
              {tabs.map((tab) => (
                <li
                  key={tab.id}
                  className={activeTab === tab.id ? 'active' : ''}
                  onClick={() => setActiveTab(tab.id)}
                >
                  {tab.label}
                </li>
              ))}
            </ul>
          </div>
          <div className="employee-card-content">
            {renderActiveTab()}
          </div>
        </>
      )}
    </div>
  );
};

export default EmployeeCard;
